import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Plus, Search, MoreHorizontal, Pencil, Trash2, Mail, Phone, Building,
  Sparkles, Check, X, PenLine, Loader2, BrainCircuit,
  ChevronLeft, ChevronRight, ShieldCheck, Layers, MessageSquare, HelpCircle,
  ArrowUpDown, MessageCircle, Eye
} from 'lucide-react';
import axios from 'axios';
import { ContactDetailPanel } from '../components/ContactDetailPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUALITY_TABS = [
  { key: 'all', label: 'Tous', icon: null, color: '' },
  { key: 'verified_crm', label: 'CRM', icon: ShieldCheck, color: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20' },
  { key: 'enriched', label: 'Enrichis', icon: Layers, color: 'text-blue-600 bg-blue-500/10 border-blue-500/20' },
  { key: 'communication', label: 'Communication', icon: MessageSquare, color: 'text-amber-600 bg-amber-500/10 border-amber-500/20' },
  { key: 'ai_suggested', label: 'IA', icon: Sparkles, color: 'text-violet-600 bg-violet-500/10 border-violet-500/20' },
  { key: 'unknown', label: 'Inconnus', icon: HelpCircle, color: 'text-gray-500 bg-gray-500/10 border-gray-500/20' },
];

const SOURCE_COLORS = {
  ringover: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/20',
  email: 'bg-rose-500/10 text-rose-700 border-rose-500/20',
  whatsapp: 'bg-green-500/10 text-green-700 border-green-500/20',
  crm_qhare: 'bg-blue-500/10 text-blue-700 border-blue-500/20',
  crm_sania: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  crm: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
  manual: 'bg-slate-500/10 text-slate-700 border-slate-500/20',
};

const SOURCE_LABELS = {
  ringover: 'Ringover', email: 'Email', whatsapp: 'WhatsApp',
  crm_qhare: 'Qhare', crm_sania: 'SAN-IA', crm: 'CRM', manual: 'Manuel',
};

function QualityBadge({ tier }) {
  const tab = QUALITY_TABS.find(t => t.key === tier);
  if (!tab || !tab.icon) return null;
  const Icon = tab.icon;
  return (
    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 gap-0.5 ${tab.color}`} data-testid={`quality-badge-${tier}`}>
      <Icon className="w-3 h-3" /> {tab.label}
    </Badge>
  );
}

function SourceBadges({ sources }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="flex gap-1 flex-wrap">
      {sources.map(src => (
        <Badge key={src} variant="outline" className={`text-[9px] px-1 py-0 ${SOURCE_COLORS[src] || SOURCE_COLORS.manual}`}>
          {SOURCE_LABELS[src] || src}
        </Badge>
      ))}
    </div>
  );
}

function ContactFormDialog({ open, onOpenChange, onSave, contact, organizations, title, submitLabel }) {
  const isEdit = !!contact;
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    company: '', job_title: '', notes: '', tags: [], organization_id: ''
  });

  useEffect(() => {
    if (open && contact) {
      setFormData({
        first_name: contact.first_name || '', last_name: contact.last_name || '',
        email: contact.email || '', phone: contact.phone || '',
        company: contact.company || '', job_title: contact.job_title || '',
        notes: contact.notes || '', tags: contact.tags || [],
        organization_id: contact.organization_id || ''
      });
    } else if (open) {
      setFormData({
        first_name: '', last_name: '', email: '', phone: '',
        company: '', job_title: '', notes: '', tags: [], organization_id: ''
      });
    }
  }, [open, contact]);

  const handleSubmit = () => {
    const payload = { ...formData };
    if (!payload.organization_id) delete payload.organization_id;
    onSave(payload, contact?.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{isEdit ? 'Mettre a jour les informations' : 'Ajouter un client ou prospect'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prenom *</Label>
              <Input value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} data-testid="contact-first-name" />
            </div>
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} data-testid="contact-last-name" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} data-testid="contact-email" />
          </div>
          <div className="space-y-2">
            <Label>Telephone</Label>
            <Input value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} data-testid="contact-phone" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Societe</Label>
              <Input value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} data-testid="contact-company" />
            </div>
            <div className="space-y-2">
              <Label>Fonction</Label>
              <Input value={formData.job_title} onChange={e => setFormData({ ...formData, job_title: e.target.value })} data-testid="contact-job-title" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Organisation</Label>
            <select value={formData.organization_id} onChange={e => setFormData({ ...formData, organization_id: e.target.value })}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="contact-organization">
              <option value="">Aucune</option>
              {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} rows={3} data-testid="contact-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!formData.first_name || !formData.last_name} data-testid="save-contact-btn">
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuggestionReviewDialog({ contact, onReview, open, onOpenChange }) {
  const sug = contact?.ai_suggestions;
  const [action, setAction] = useState(null);
  const [modName, setModName] = useState('');
  const [modCompany, setModCompany] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (sug && open) {
      setModName(sug.suggested_name || '');
      setModCompany(sug.suggested_company || '');
      setAction(null);
    }
  }, [sug, open]);

  if (!sug) return null;

  const handleSubmit = async (act) => {
    setLoading(true);
    try {
      await onReview(contact.id, act, act === 'modify' ? modName : null, act === 'modify' ? modCompany : null);
      onOpenChange(false);
    } finally { setLoading(false); }
  };

  const pct = Math.round((sug.confidence || 0) * 100);
  const barColor = pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BrainCircuit className="w-5 h-5 text-violet-500" /> Suggestion IA</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="p-3 rounded-lg bg-muted/50 border">
            <p className="text-xs text-muted-foreground mb-1">Nom actuel</p>
            <p className="font-medium">{contact.first_name} {contact.last_name}</p>
          </div>
          <div className="p-3 rounded-lg bg-violet-500/5 border border-violet-500/20">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-violet-600">Suggestion IA</p>
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden"><div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} /></div>
                <span className="text-xs text-muted-foreground">{pct}%</span>
              </div>
            </div>
            {sug.suggested_name && <p className="font-medium">{sug.suggested_name}</p>}
            {sug.suggested_company && <p className="text-sm text-muted-foreground flex items-center gap-1"><Building className="w-3.5 h-3.5" /> {sug.suggested_company}</p>}
            {sug.reasoning && <p className="text-xs text-muted-foreground mt-2 italic">{sug.reasoning}</p>}
          </div>
          {action === 'modify' && (
            <div className="space-y-3 p-3 rounded-lg border">
              <div className="space-y-2"><Label className="text-xs">Nom complet</Label><Input value={modName} onChange={e => setModName(e.target.value)} data-testid="modify-name-input" /></div>
              <div className="space-y-2"><Label className="text-xs">Societe</Label><Input value={modCompany} onChange={e => setModCompany(e.target.value)} data-testid="modify-company-input" /></div>
            </div>
          )}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          {action === 'modify' ? (
            <>
              <Button variant="outline" onClick={() => setAction(null)} disabled={loading}>Retour</Button>
              <Button onClick={() => handleSubmit('modify')} disabled={loading || !modName} data-testid="confirm-modify-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Appliquer
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => handleSubmit('reject')} disabled={loading} className="text-destructive" data-testid="reject-suggestion-btn"><X className="w-4 h-4 mr-1" /> Ignorer</Button>
              <Button variant="outline" size="sm" onClick={() => setAction('modify')} disabled={loading} data-testid="modify-suggestion-btn"><PenLine className="w-4 h-4 mr-1" /> Modifier</Button>
              <Button size="sm" onClick={() => handleSubmit('accept')} disabled={loading} data-testid="accept-suggestion-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Valider
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function ContactsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';

  const [contacts, setContacts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState(false);

  // Filters & pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeQuality, setActiveQuality] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;

  const [mergeSuggestions, setMergeSuggestions] = useState([]);
  const [scanning, setScanning] = useState(false);

  // Dialogs
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [reviewContact, setReviewContact] = useState(null);
  const [detailContact, setDetailContact] = useState(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage, sort_by: sortBy };
      if (searchQuery) params.search = searchQuery;
      if (activeQuality !== 'all') params.quality = activeQuality;

      const { data } = await axios.get(`${API}/contacts`, {
        headers: { Authorization: `Bearer ${token}` }, params
      });
      setContacts(data.contacts || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch {
      toast.error('Erreur de chargement des contacts');
    } finally {
      setLoading(false);
    }
  }, [token, page, perPage, searchQuery, activeQuality, sortBy]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/contacts/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(data);
    } catch {}
  }, [token]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const { data } = await axios.get(`${API}/organizations`, { headers: { Authorization: `Bearer ${token}` } });
        setOrganizations(data);
      } catch {}
    };
    const fetchMergeSuggestions = async () => {
      try {
        const { data } = await axios.get(`${API}/contacts/merge-suggestions`, { headers: { Authorization: `Bearer ${token}` } });
        setMergeSuggestions(data);
      } catch {}
    };
    fetchOrgs();
    if (isAdmin) fetchMergeSuggestions();
  }, [token, isAdmin]);

  const handleCreate = async (payload) => {
    try {
      await axios.post(`${API}/contacts`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contact cree');
      setIsCreateOpen(false);
      fetchContacts();
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleUpdate = async (payload, contactId) => {
    try {
      await axios.patch(`${API}/contacts/${contactId}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contact mis a jour');
      setEditContact(null);
      fetchContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try {
      await axios.delete(`${API}/contacts/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contact supprime');
      fetchContacts();
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleMergeAction = async (suggestionId, action) => {
    try {
      await axios.post(`${API}/contacts/merge-suggestions/${suggestionId}/${action}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(action === 'accept' ? 'Contacts fusionnes' : 'Suggestion ignoree');
      // Reload suggestions from server (auto-dismissed ones will be gone)
      const res = await axios.get(`${API}/contacts/merge-suggestions`, { headers: { Authorization: `Bearer ${token}` } });
      setMergeSuggestions(res.data);
      if (action === 'accept') { fetchContacts(); fetchStats(); }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleScanDuplicates = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/contacts/merge-suggestions/scan`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Scan des doublons lance en arriere-plan...');
      // Poll suggestions after a delay to let background task complete
      setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/contacts/merge-suggestions`, { headers: { Authorization: `Bearer ${token}` } });
          setMergeSuggestions(res.data);
          if (res.data.length > 0) toast.info(`${res.data.length} doublon(s) detecte(s)`);
        } catch {}
        setScanning(false);
      }, 8000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de scan');
      setScanning(false);
    }
  };

  const handleEnrich = async () => {
    setEnriching(true);
    try {
      const { data } = await axios.post(`${API}/admin/enrich-contacts`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (data.enriched > 0) toast.success(`${data.enriched} contacts enrichis par l'IA`);
      else toast.info('Aucun nouveau contact a enrichir');
      fetchContacts();
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur d'enrichissement"); }
    finally { setEnriching(false); }
  };

  const handleReview = async (contactId, action, modName, modCompany) => {
    try {
      await axios.post(`${API}/admin/review-suggestion/${contactId}`,
        { action, modified_name: modName, modified_company: modCompany },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Suggestion ${action === 'accept' ? 'validee' : action === 'modify' ? 'modifiee' : 'ignoree'}`);
      fetchContacts();
      fetchStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleTabChange = (key) => {
    setActiveQuality(key);
    setPage(1);
  };

  const cycleSortBy = () => {
    const order = ['name', 'created_at', 'last_interaction'];
    const idx = order.indexOf(sortBy);
    setSortBy(order[(idx + 1) % order.length]);
    setPage(1);
  };

  const sortLabel = { name: 'Nom', created_at: 'Date creation', last_interaction: 'Derniere interaction' }[sortBy];

  return (
    <DashboardLayout title="Contacts">
      <div className="space-y-4" data-testid="contacts-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground">{total} contact{total !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher nom, tel, email..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9 w-64 h-9"
                data-testid="search-contacts"
              />
            </div>
            <Button variant="outline" size="sm" onClick={cycleSortBy} className="gap-1.5 h-9 text-xs" data-testid="sort-toggle">
              <ArrowUpDown className="w-3.5 h-3.5" /> {sortLabel}
            </Button>
            {isAdmin && (
              <>
                <Button variant="outline" size="sm" onClick={handleScanDuplicates} disabled={scanning} className="gap-1.5 h-9" data-testid="scan-duplicates-btn">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />} Scanner doublons
                </Button>
                <Button variant="outline" size="sm" onClick={handleEnrich} disabled={enriching} className="gap-1.5 h-9" data-testid="enrich-contacts-btn">
                  {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Enrichir IA
                </Button>
              </>
            )}
            <Button size="sm" onClick={() => setIsCreateOpen(true)} className="gap-1.5 h-9" data-testid="create-contact-btn">
              <Plus className="w-4 h-4" /> Nouveau
            </Button>
          </div>
        </div>

        {/* Quality Tabs */}
        <div className="flex gap-1.5 flex-wrap" data-testid="quality-tabs">
          {QUALITY_TABS.map(tab => {
            const count = tab.key === 'all' ? (stats.all || 0) : (stats[tab.key] || 0);
            const isActive = activeQuality === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border
                  ${isActive
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-card text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground'}`}
                data-testid={`quality-tab-${tab.key}`}
              >
                {tab.icon && <tab.icon className="w-3.5 h-3.5" />}
                {tab.label}
                <span className={`ml-1 text-[10px] px-1.5 py-0 rounded-full ${isActive ? 'bg-primary-foreground/20' : 'bg-muted'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Merge Suggestions Banner */}
        {isAdmin && mergeSuggestions.length > 0 && (
          <Card className="border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-orange-500/5" data-testid="merge-suggestions-banner">
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4 text-amber-600" />
                <p className="text-sm font-medium">{mergeSuggestions.length} doublon{mergeSuggestions.length > 1 ? 's' : ''} potentiel{mergeSuggestions.length > 1 ? 's' : ''} detecte{mergeSuggestions.length > 1 ? 's' : ''}</p>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {mergeSuggestions.map(sug => (
                  <div key={sug.id} className="flex items-center gap-3 text-xs bg-card rounded-lg border px-3 py-2" data-testid={`merge-suggestion-${sug.id}`}>
                    <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-1 gap-y-0.5">
                      <span className="font-medium">{sug.incoming_name}</span>
                      {sug.incoming_source && <span className="inline-flex px-1.5 py-0 rounded text-[9px] font-medium bg-cyan-500/15 text-cyan-400 border border-cyan-500/20">{sug.incoming_source}</span>}
                      {sug.incoming_phone && <span className="text-muted-foreground text-[10px]">({sug.incoming_phone})</span>}
                      <span className="text-muted-foreground mx-1">≈</span>
                      <span className="font-medium">{sug.contact_name}</span>
                      {sug.contact_source && <span className="inline-flex px-1.5 py-0 rounded text-[9px] font-medium bg-violet-500/15 text-violet-400 border border-violet-500/20">{sug.contact_source}</span>}
                      {sug.contact_phone && <span className="text-muted-foreground text-[10px]">({sug.contact_phone})</span>}
                      <span className="text-muted-foreground ml-1">({Math.round(sug.similarity * 100)}%)</span>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleMergeAction(sug.id, 'accept')} className="h-6 text-xs gap-1 px-2" data-testid={`merge-accept-${sug.id}`}>
                      <Check className="w-3 h-3" /> Fusionner
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleMergeAction(sug.id, 'reject')} className="h-6 text-xs gap-1 px-2 text-muted-foreground" data-testid={`merge-reject-${sug.id}`}>
                      <X className="w-3 h-3" /> Ignorer
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts Table */}
        <Card data-testid="contacts-table-card">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Societe</TableHead>
                  <TableHead>Email / Tel</TableHead>
                  <TableHead>Sources</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Chargement...</TableCell></TableRow>
                ) : contacts.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">{searchQuery ? 'Aucun contact trouve' : 'Aucun contact dans cette categorie'}</TableCell></TableRow>
                ) : (
                  contacts.map(contact => {
                    const initials = `${(contact.first_name || '?')[0]}${(contact.last_name || '?')[0]}`.toUpperCase();
                    const tier = contact.quality_tier || 'communication';
                    const hasSuggestion = contact.ai_suggestions?.needs_review;
                    const avatarBg = {
                      verified_crm: 'bg-emerald-500/10 text-emerald-600',
                      enriched: 'bg-blue-500/10 text-blue-600',
                      ai_suggested: 'bg-violet-500/10 text-violet-600',
                      unknown: 'bg-gray-500/10 text-gray-500',
                      communication: 'bg-amber-500/10 text-amber-600',
                    }[tier] || 'bg-amber-500/10 text-amber-600';

                    return (
                      <TableRow key={contact.id} className="group cursor-pointer hover:bg-muted/50 transition-colors" data-testid={`contact-row-${contact.id}`}
                        onClick={() => setDetailContact(contact)}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar className="h-9 w-9">
                              <AvatarFallback className={avatarBg}>{initials}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate(`/chat/global?scope=contact&scopeId=${contact.id}&scopeName=${encodeURIComponent(`${contact.first_name} ${contact.last_name}`.trim())}`); }}
                                  className="font-medium text-sm hover:underline hover:text-primary transition-colors text-left"
                                  data-testid={`contact-chat-link-${contact.id}`}
                                >
                                  {contact.first_name} {contact.last_name}
                                </button>
                                <QualityBadge tier={tier} />
                                {hasSuggestion && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-violet-500/30 text-violet-600 cursor-pointer hover:bg-violet-500/10"
                                    onClick={(e) => { e.stopPropagation(); setReviewContact(contact); }} data-testid={`suggestion-badge-${contact.id}`}>
                                    <Sparkles className="w-3 h-3 mr-0.5" /> Suggestion
                                  </Badge>
                                )}
                              </div>
                              {contact.job_title && <p className="text-xs text-muted-foreground">{contact.job_title}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {contact.organization_name || contact.company ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (contact.organization_id) {
                                  navigate(`/chat/global?scope=organization&scopeId=${contact.organization_id}&scopeName=${encodeURIComponent(contact.organization_name || contact.company)}`);
                                }
                              }}
                              className={`flex items-center gap-1.5 text-sm text-muted-foreground ${contact.organization_id ? 'hover:underline hover:text-primary cursor-pointer' : 'cursor-default italic'} transition-colors`}
                              data-testid={`contact-org-link-${contact.id}`}
                            >
                              <Building className="w-3.5 h-3.5 shrink-0" />
                              <span>{contact.organization_name || contact.company}</span>
                            </button>
                          ) : <span className="text-muted-foreground text-xs">-</span>}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {/* Show all emails from emails[] list, fallback to single email field */}
                            {(contact.emails && contact.emails.length > 0) ? (
                              contact.emails.map((e, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <Mail className="w-3 h-3 shrink-0" />
                                  <span className="truncate max-w-[180px]">{e.address}</span>
                                  {e.label && e.label !== 'principal' && <span className="text-[9px] opacity-60">({e.label})</span>}
                                </div>
                              ))
                            ) : contact.email ? (
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Mail className="w-3 h-3" />{contact.email}</div>
                            ) : null}
                            {/* Show all phones from phones[] list, fallback to single phone field */}
                            {(contact.phones && contact.phones.length > 0) ? (
                              contact.phones.map((p, i) => (
                                <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                  <Phone className="w-3 h-3 shrink-0" />
                                  <span>{p.number}</span>
                                  {p.label && p.label !== 'principal' && <span className="text-[9px] opacity-60">({p.label})</span>}
                                </div>
                              ))
                            ) : contact.phone ? (
                              <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Phone className="w-3 h-3" />{contact.phone}</div>
                            ) : null}
                            {!contact.email && !contact.phone && !(contact.emails?.length) && !(contact.phones?.length) && <span className="text-muted-foreground text-xs">-</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <SourceBadges sources={contact.source_badges} />
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`contact-actions-${contact.id}`}
                                onClick={(e) => e.stopPropagation()}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDetailContact(contact); }} data-testid={`view-contact-${contact.id}`}>
                                <Eye className="w-4 h-4 mr-2" /> Voir le detail
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/chat/global?scope=contact&scopeId=${contact.id}&scopeName=${encodeURIComponent(`${contact.first_name} ${contact.last_name}`.trim())}`); }} data-testid={`chat-contact-${contact.id}`}>
                                <MessageCircle className="w-4 h-4 mr-2" /> Discuter
                              </DropdownMenuItem>
                              {hasSuggestion && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setReviewContact(contact); }} data-testid={`review-ai-${contact.id}`}>
                                  <BrainCircuit className="w-4 h-4 mr-2" /> Examiner suggestion IA
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditContact(contact); }} data-testid={`edit-contact-${contact.id}`}>
                                <Pencil className="w-4 h-4 mr-2" /> Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(contact.id); }} className="text-destructive" data-testid={`delete-contact-${contact.id}`}>
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between" data-testid="pagination">
            <p className="text-xs text-muted-foreground">
              Page {page} sur {totalPages} ({total} contacts)
            </p>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="h-8 gap-1" data-testid="prev-page">
                <ChevronLeft className="w-4 h-4" /> Precedent
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="h-8 gap-1" data-testid="next-page">
                Suivant <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Dialogs */}
        <ContactFormDialog
          open={isCreateOpen}
          onOpenChange={setIsCreateOpen}
          onSave={handleCreate}
          contact={null}
          organizations={organizations}
          title="Nouveau contact"
          submitLabel="Creer"
        />
        <ContactFormDialog
          open={!!editContact}
          onOpenChange={(open) => { if (!open) setEditContact(null); }}
          onSave={handleUpdate}
          contact={editContact}
          organizations={organizations}
          title="Modifier le contact"
          submitLabel="Mettre a jour"
        />
        <SuggestionReviewDialog
          contact={reviewContact}
          onReview={handleReview}
          open={!!reviewContact}
          onOpenChange={(open) => { if (!open) setReviewContact(null); }}
        />
        <ContactDetailPanel
          contact={detailContact}
          open={!!detailContact}
          onOpenChange={(open) => { if (!open) setDetailContact(null); }}
          token={token}
        />
      </div>
    </DashboardLayout>
  );
}
